import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Target,
  TrendingUp,
  Users,
  Globe,
  BarChart3,
  Zap,
  Mail,
  Phone,
  CheckCircle2,
  MessageSquare
} from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";

export default function Advertise() {
  const adLocations = [
    {
      name: "홈페이지 상단",
      description: "메인 페이지 최상단에 노출되는 프리미엄 광고 위치",
      pcSize: "728x90 또는 970x90",
      mobileSize: "320x50 또는 320x100",
      traffic: "최대 트래픽",
      visibility: "높음",
    },
    {
      name: "사이드바",
      description: "모든 페이지에서 지속적으로 노출되는 사이드바 광고",
      pcSize: "300x250 또는 160x600",
      mobileSize: "300x250",
      traffic: "중간 트래픽",
      visibility: "중간",
    },
    {
      name: "기사 상단",
      description: "기사 내용 바로 위에 노출되는 타겟 광고",
      pcSize: "728x90 또는 468x60",
      mobileSize: "320x50",
      traffic: "높은 트래픽",
      visibility: "높음",
    },
    {
      name: "기사 하단",
      description: "기사를 읽은 후 노출되는 광고 위치",
      pcSize: "728x90 또는 300x250",
      mobileSize: "320x50 또는 300x250",
      traffic: "중간 트래픽",
      visibility: "중간",
    },
    {
      name: "모바일 하단",
      description: "모바일 화면 하단에 고정되는 광고",
      pcSize: "728x90",
      mobileSize: "320x50 또는 320x100",
      traffic: "모바일 전용",
      visibility: "높음",
    },
  ];

  const pricingPlans = [
    {
      name: "정액제 (Fixed)",
      description: "월 고정 금액으로 무제한 노출",
      benefits: ["예산 관리 용이", "무제한 노출", "안정적인 비용"],
      recommended: "브랜드 인지도 향상",
    },
    {
      name: "클릭당 과금 (CPC)",
      description: "광고 클릭 시에만 비용 발생",
      benefits: ["실제 클릭에만 과금", "ROI 측정 용이", "비용 효율적"],
      recommended: "전환율 중시 캠페인",
    },
    {
      name: "노출당 과금 (CPM)",
      description: "1,000회 노출당 비용 발생",
      benefits: ["대규모 노출", "브랜드 인지도", "유연한 예산"],
      recommended: "대규모 브랜딩 캠페인",
    },
  ];

  const features = [
    {
      icon: Target,
      title: "타겟 광고",
      description: "기사 카테고리 및 태그 기반 정밀 타겟팅으로 관련성 높은 독자에게 노출",
    },
    {
      icon: Users,
      title: "호주 한인 커뮤니티",
      description: "호주 거주 한인들을 대상으로 한 특화된 광고 플랫폼",
    },
    {
      icon: TrendingUp,
      title: "실시간 통계",
      description: "노출수, 클릭수, CTR 등 광고 성과를 실시간으로 확인",
    },
    {
      icon: Globe,
      title: "PC/모바일 최적화",
      description: "각 디바이스에 최적화된 이미지로 최상의 광고 효과",
    },
    {
      icon: BarChart3,
      title: "우선순위 시스템",
      description: "광고 우선순위 설정으로 중요한 캠페인 우선 노출",
    },
    {
      icon: Zap,
      title: "빠른 승인 및 게재",
      description: "간편한 등록 절차와 빠른 광고 승인 프로세스",
    },
  ];

  return (
    <PageContainer size="wide">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary/10 to-background py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-4">
              OZK News 광고 제휴 서비스
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              호주 한인 커뮤니티와<br />함께 성장하세요
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              OZK News는 호주 거주 한인들을 위한 프리미엄 뉴스 플랫폼입니다.<br />
              효과적인 광고 및 제휴로 귀사의 비즈니스를 성장시키세요.<br />
              광고 및 제휴 문의는 이메일로 연락 주시기 바랍니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:ozknews.com@gmail.com">
                <Button size="lg" data-testid="button-contact-email">
                  <Mail className="mr-2 h-5 w-5" />
                  이메일 문의
                </Button>
              </a>
              <a href="tel:+61450760790">
                <Button size="lg" variant="outline" data-testid="button-contact-phone">
                  <Phone className="mr-2 h-5 w-5" />
                  전화 문의
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="py-16">
        {/* Features Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">OZK News 광고의 장점</h2>
            <p className="text-muted-foreground text-lg">
              효과적이고 투명한 광고 솔루션을 제공합니다
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <Card key={feature.title} className="hover-elevate">
                <CardHeader>
                  <feature.icon className="h-10 w-10 text-primary mb-4" />
                  <CardTitle>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <Separator className="my-16" />

        {/* Ad Locations Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">광고 위치 및 규격</h2>
            <p className="text-muted-foreground text-lg">
              각 위치별 최적화된 광고 규격을 제공합니다
            </p>
          </div>

          <div className="space-y-6">
            {adLocations.map((location, index) => (
              <Card key={location.name}>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-1">
                      <div className="flex items-start gap-2 mb-2">
                        <Badge variant="outline" className="mt-1">
                          {index + 1}
                        </Badge>
                        <div>
                          <h3 className="font-semibold text-lg mb-1">
                            {location.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {location.description}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2 grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">
                          PC 권장 크기
                        </p>
                        <p className="font-mono text-sm bg-muted px-3 py-2 rounded">
                          {location.pcSize}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">
                          모바일 권장 크기
                        </p>
                        <p className="font-mono text-sm bg-muted px-3 py-2 rounded">
                          {location.mobileSize}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">
                          트래픽
                        </p>
                        <Badge variant="secondary">{location.traffic}</Badge>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">
                          가시성
                        </p>
                        <Badge variant="secondary">{location.visibility}</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <Separator className="my-16" />

        {/* Pricing Plans Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">과금 방식</h2>
            <p className="text-muted-foreground text-lg">
              캠페인 목표에 맞는 최적의 과금 방식을 선택하세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pricingPlans.map((plan) => (
              <Card key={plan.name} className="hover-elevate">
                <CardHeader>
                  <CardTitle>{plan.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {plan.description}
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium mb-2">주요 혜택:</p>
                    <ul className="space-y-2">
                      {plan.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-sm">
                      <span className="font-medium">추천:</span>{" "}
                      <span className="text-muted-foreground">{plan.recommended}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <Separator className="my-16" />

        {/* Target Advertising Section */}
        <section className="mb-20">
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Target className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">타겟 광고 시스템</h3>
                  <p className="text-muted-foreground text-lg">
                    더 정확한 타겟팅으로 광고 효과를 극대화하세요
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    카테고리별 타겟팅
                  </h4>
                  <p className="text-sm text-muted-foreground pl-7">
                    주요뉴스, 교민생활, 비즈니스/경제, 문화/교육, 투자/부동산, 이민/유학, 스포츠/게임 등 8개 카테고리별 맞춤 광고
                  </p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    태그 기반 매칭
                  </h4>
                  <p className="text-sm text-muted-foreground pl-7">
                    "호주", "부동산", "투자" 등 태그를 설정하면 관련 기사에 우선 노출되어 광고 효과 50% 증가
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <Card className="bg-primary text-primary-foreground">
            <CardContent className="p-12">
              <MessageSquare className="h-16 w-16 mx-auto mb-6 opacity-90" />
              <h2 className="text-3xl font-bold mb-4">
                지금 바로 광고 및 제휴를 시작하세요
              </h2>
              <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
                OZK News와 함께 호주 한인 커뮤니티에 귀사의 메시지를 전달하세요.<br />
                광고 및 제휴 문의는 이메일로 연락 주시면 상세한 안내와 맞춤 제안을 드립니다.
              </p>

              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="mailto:ozknews.com@gmail.com">
                    <Button size="lg" variant="secondary" data-testid="button-cta-email">
                      <Mail className="mr-2 h-5 w-5" />
                      ozknews.com@gmail.com
                    </Button>
                  </a>
                  <a href="tel:+61450760790">
                    <Button size="lg" variant="secondary" data-testid="button-cta-phone">
                      <Phone className="mr-2 h-5 w-5" />
                      +61-450 760 790
                    </Button>
                  </a>
                </div>

                <p className="text-sm opacity-75">
                  평일 09:00 - 18:00 (호주 시드니 시간)
                </p>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </PageContainer>
  );
}
